import { useCallback, useEffect, useState } from "react";
import { Eye, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["new", "contacted", "appointment", "sold", "closed"];

const statusColors = {
  new: "border-[#FF6B00]/50 text-[#FF8533]",
  contacted: "border-blue-500/40 text-blue-600",
  appointment: "border-green-500/40 text-green-600",
  sold: "border-emerald-500/40 text-emerald-400",
  closed: "border-zinc-300 text-zinc-500",
};

const typeLabels = { test_drive: "Test Drive", quote: "Quote", general_inquiry: "Inquiry" };

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads");
      setLeads(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateStatus = async (leadId, status) => {
    try {
      await api.patch(`/admin/leads/${leadId}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
      toast.success("Lead status updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (leadId) => {
    try {
      await api.delete(`/admin/leads/${leadId}`);
      setLeads((ls) => ls.filter((l) => l.id !== leadId));
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-leads">
      <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-1">Leads</h1>
      <p className="text-sm text-zinc-500 mb-6" data-testid="admin-leads-count">{leads.length} total leads</p>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} className="bg-white border border-zinc-200 p-4 flex flex-wrap items-center gap-4" data-testid={`admin-lead-${l.id}`}>
              <Badge className="bg-[#FF6B00]/15 border border-[#FF6B00]/30 text-[#FF8533] uppercase text-[10px] tracking-wider rounded-sm shrink-0">
                {typeLabels[l.lead_type] || l.lead_type}
              </Badge>
              <div className="flex-1 min-w-[180px]">
                <p className="font-bold text-zinc-900">{l.name}</p>
                <p className="text-xs text-zinc-500">{l.email} · {l.phone}</p>
                {l.vehicle_title && <p className="text-xs text-[#FF6B00] font-mono mt-0.5">{l.vehicle_title}</p>}
              </div>
              <span className="text-xs text-zinc-500 shrink-0">{fmtDate(l.created_at)}</span>
              <select
                value={l.status}
                onChange={(e) => updateStatus(l.id, e.target.value)}
                className={`h-9 rounded-sm border bg-white px-3 text-xs font-bold uppercase tracking-wider focus:outline-none ${statusColors[l.status] || "border-zinc-300 text-zinc-600"}`}
                data-testid={`lead-status-select-${l.id}`}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button onClick={() => setViewing(l)} className="p-2 border border-zinc-300 rounded-sm text-zinc-500 hover:text-[#FF6B00] hover:border-[#FF6B00]" aria-label={`View lead from ${l.name}`} data-testid={`view-lead-${l.id}`}>
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(l.id)} className="p-2 border border-zinc-300 rounded-sm text-zinc-500 hover:text-red-600 hover:border-red-500" aria-label={`Delete lead from ${l.name}`} data-testid={`delete-lead-${l.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          {leads.length === 0 && (
            <div className="border border-dashed border-zinc-200 p-14 text-center text-zinc-500" data-testid="admin-leads-empty">
              No leads yet. Submissions from test drive, quote, and contact forms will appear here.
            </div>
          )}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-lg" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-zinc-900">
              {typeLabels[viewing?.lead_type] || "Lead"} — {viewing?.name}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              {[
                ["Email", viewing.email], ["Phone", viewing.phone],
                ["Vehicle", viewing.vehicle_title],
                ["Preferred Date", viewing.preferred_date], ["Preferred Time", viewing.preferred_time],
                ["Trade-In", viewing.trade_in_details],
                ["Down Payment", viewing.down_payment != null ? `$${Number(viewing.down_payment).toLocaleString()}` : null],
                ["Submitted", fmtDate(viewing.created_at)],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-zinc-200 pb-2">
                  <span className="text-zinc-500 font-mono uppercase text-xs">{k}</span>
                  <span className="text-zinc-800 text-right">{v}</span>
                </div>
              ))}
              {viewing.message && (
                <div>
                  <p className="text-zinc-500 font-mono uppercase text-xs mb-1">Message</p>
                  <p className="text-zinc-600 italic">"{viewing.message}"</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
